import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { executeRecaptcha } from '../../services/recaptcha';

/* ============================= Shared split layout ============================= */

function AuthShell({ side, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', minHeight: '100vh' }} className="auth-shell">
      {/* Form column */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 24px', background: '#fff' }}>
        <div style={{ width: '100%', maxWidth: 440 }}>{children}</div>
      </div>

      {/* Brand column */}
      <div style={{ background: 'var(--hero-gradient)', color: '#fff', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 48px' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-20%', width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.35) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-35%', left: '-15%', width: 560, height: 560, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: 480 }}>{side}</div>
      </div>

      <style>{`
        @media (max-width: 960px) {
          .auth-shell { grid-template-columns: 1fr !important; }
          .auth-shell > div:last-child { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function BrandPanel({ title, titleAccent, lead, points }) {
  return (
    <>
      <img src="/images/logo-white.svg" alt="PerkVora" style={{ height: 40, marginBottom: 40 }}
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
      />
      <div style={{ display: 'none', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', fontWeight: 800, fontSize: 32, letterSpacing: '-0.025em', color: '#fff', marginBottom: 40, lineHeight: 1 }}>
        Perk<span className="gradient-text">Vora</span>
      </div>

      <h2 style={{ fontSize: 'clamp(30px, 3.4vw, 42px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: 18 }}>
        {title} <span className="gradient-text">{titleAccent}</span>
      </h2>
      <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.65, marginBottom: 36 }}>{lead}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {points.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(255,255,255,0.08)', padding: '14px 18px', borderRadius: 14, border: '1px solid rgba(255,255,255,0.14)', backdropFilter: 'blur(8px)' }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{p.icon}</span>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</p>
              <p style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 40, paddingTop: 28, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex' }}>
            {['J','M','P','S'].map((l, i) => (
              <div key={l} style={{
                width: 36, height: 36, borderRadius: '50%',
                background: ['#6B2FD9','#EC4899','#F97316','#F59E0B'][i],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 13,
                border: '2px solid #1A1B4B',
                marginLeft: i === 0 ? 0 : -10,
              }}>{l}</div>
            ))}
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600 }}>Joined by 10,000+ smart shoppers</p>
            <p style={{ fontSize: 12, opacity: 0.7 }}>★ ★ ★ ★ ★  4.8 average rating</p>
          </div>
        </div>
      </div>
    </>
  );
}

function AuthHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', marginBottom: 28 }}>
        <img src="/images/logo.svg" alt="PerkVora" style={{ height: 32 }}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
        />
        <span style={{ display: 'none', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: '#0F1225' }}>
          Perk<span className="gradient-text">Vora</span>
        </span>
      </Link>
      <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 8, lineHeight: 1.15 }}>{title}</h1>
      <p style={{ color: '#4A5568', fontSize: 15, lineHeight: 1.5 }}>{subtitle}</p>
    </div>
  );
}

function SocialButtons() {
  const [pending, setPending] = useState(null); // 'Google' | 'Apple' | null

  // Hide the inline notice after a few seconds so it doesn't linger.
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => setPending(null), 4000);
    return () => clearTimeout(t);
  }, [pending]);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <button type="button" onClick={() => setPending('Google')}
          style={{ padding: '12px 14px', background: '#fff', border: '1.5px solid #E4E7F0', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0F1225', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit', transition: 'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#F7F8FC'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          <GoogleIcon /> Google
        </button>
        <button type="button" onClick={() => setPending('Apple')}
          style={{ padding: '12px 14px', background: '#0F1225', border: '1.5px solid #0F1225', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit' }}
        >
          <AppleIcon /> Apple
        </button>
      </div>

      {pending && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)',
            border: '1px solid rgba(107,47,217,0.15)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 14,
            fontSize: 13, color: '#0F1225', lineHeight: 1.45,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1.2 }}>⏳</span>
          <span>
            <strong>{pending} sign-in is coming soon.</strong> For now, please continue with email below.
          </span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 18px' }}>
        <div style={{ flex: 1, height: 1, background: '#E4E7F0' }} />
        <span style={{ fontSize: 12, color: '#8B94A8', fontWeight: 500 }}>or continue with email</span>
        <div style={{ flex: 1, height: 1, background: '#E4E7F0' }} />
      </div>
    </>
  );
}

function PasswordInput({ value, onChange, placeholder, autoFocus, required = true }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" type={show ? 'text' : 'password'} required={required} autoFocus={autoFocus}
        value={value} onChange={onChange} placeholder={placeholder}
        style={{ paddingRight: 52 }}
      />
      <button type="button" onClick={() => setShow(v => !v)}
        style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#8B94A8', fontWeight: 600 }}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function PasswordStrength({ password }) {
  const score = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (password.length >= 12) s++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return Math.min(s, 4);
  })();
  const labels = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['#DC2626', '#F59E0B', '#F97316', '#6B2FD9', '#0F9D6E'];
  if (!password) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i < score ? colors[score] : '#E4E7F0', transition: 'background 0.2s' }} />
        ))}
      </div>
      <p style={{ fontSize: 11, color: colors[score], fontWeight: 600 }}>{labels[score]}</p>
    </div>
  );
}

/* ============================= LOGIN ============================= */

export function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm]   = useState({ email: '', password: '' });
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  // `from` is set by ProtectedRoute when it bounces an unauthed user here.
  // Null when they navigated to /login directly (e.g. via the Sign In button
  // in the navbar). In that case the post-login destination is role-based:
  // regular users go to the deal feed so the logged-in state is immediately
  // useful, not the marketing splash they were already looking at.
  const from = location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(form.email, form.password);
    if (result.success) {
      const role = result.user?.role;
      let target;
      if (role === 'super_admin')       target = '/admin';
      else if (role === 'merchant_admin') target = '/merchant';
      else                                target = from || '/coupons';
      navigate(target, { replace: true });
    } else if (result.requiresVerification) {
      // The credentials were correct but email isn't verified. Backend has
      // already re-issued a fresh OTP — bounce to the verify page with the
      // email + dev_otp pre-loaded so the user doesn't have to retype.
      navigate('/verify-otp', {
        replace: true,
        state: { email: result.email, devOtp: result.devOtp, from, next: 'login' },
      });
    } else {
      setError(result.error);
    }
  };

  return (
    <AuthShell side={
      <BrandPanel
        title="Welcome back to"
        titleAccent="real savings."
        lead="Sign in to track your cashback, unlock member-only deals, and pick up right where you left off."
        points={[
          { icon: '💰', title: 'Cashback earnings', desc: 'See what you\'ve earned and cash out anytime' },
          { icon: '⭐', title: 'Personalized feed', desc: 'Deals curated from your shopping history' },
          { icon: '🎯', title: 'Saved favorites', desc: 'All your bookmarked deals in one place' },
        ]}
      />
    }>
      <AuthHeader title="Sign in" subtitle="Welcome back — let's get you some savings." />

      <SocialButtons />

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Email address</label>
          <input className="form-input" type="text" inputMode="email" autoComplete="email" required autoFocus value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com" />
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Password</label>
            <Link to="/forgot-password" style={{ fontSize: 12, color: '#6B2FD9', fontWeight: 600 }}>Forgot?</Link>
          </div>
          <PasswordInput
            value={form.password}
            onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
            placeholder="••••••••"
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4A5568', marginBottom: 20, cursor: 'pointer' }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#6B2FD9' }} />
          Keep me signed in for 30 days
        </label>

        <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign In →'}
        </button>
      </form>

      <p style={{ textAlign: 'center', fontSize: 14, color: '#4A5568', marginTop: 28 }}>
        New to PerkVora?{' '}
        <Link to="/register" state={{ from }} style={{ color: '#6B2FD9', fontWeight: 600 }}>Create your free account</Link>
      </p>
    </AuthShell>
  );
}

/* ============================= REGISTER ============================= */

export function RegisterPage() {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const intendedRole = searchParams.get('role');
  const intendedPlan = searchParams.get('plan');
  // Picked up from ?ref= (the share-link form) and stored on the form so the
  // user can also edit / paste a code received outside the link (Ananya
  // 06-30 #8). Without sending this back to /auth/register the backend's
  // attachReferee() never fired, so the referrer's Invited count stayed at
  // 0 even after a successful linked signup (#9).
  const refFromUrl   = searchParams.get('ref') || '';
  const [form, setForm]   = useState({ name: '', email: '', password: '', password_confirmation: '', ref: refFromUrl });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [errors, setErrors] = useState({});
  const [showRefInput, setShowRefInput] = useState(!!refFromUrl);
  const from = location.state?.from;

  const isMerchant = intendedRole === 'merchant';
  const isPartner  = intendedRole === 'partner';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (form.password !== form.password_confirmation) { setErrors({ password_confirmation: 'Passwords do not match' }); return; }
    if (form.password.length < 8) { setErrors({ password: 'Password must be at least 8 characters' }); return; }
    if (!acceptTerms) { setErrors({ terms: 'Please accept the terms to continue' }); return; }
    // Normalize email to lowercase before send so it matches the backend
    // dedupe behavior and a user who typed JANE@X.COM sees the same
    // verified row when they come back to sign in (Ananya 06-30 #10/#12).
    const payload = {
      ...form,
      email: (form.email || '').trim().toLowerCase(),
      ref: (form.ref || '').trim().toUpperCase() || undefined,
      role: intendedRole,
      plan: intendedPlan,
      marketing_opt_in: marketing,
    };
    const result = await register(payload);
    if (result.success && result.requiresVerification) {
      // Every fresh signup is OTP-gated now. Route to /verify-otp with the
      // email + dev_otp + intended destination so we can resume the post-
      // verify navigation (merchant onboarding / partner / deal feed)
      // after the code lands.
      const intendedTarget = isMerchant ? (from || '/merchant')
                            : isPartner  ? (from || '/admin')
                            :              (from || '/coupons');
      navigate('/verify-otp', {
        replace: true,
        state: { email: result.email, devOtp: result.devOtp, from: intendedTarget, next: 'register' },
      });
    } else if (result.success) {
      // Backward-compatible fallthrough — shouldn't fire under the OTP flow
      // but is here in case a future server change goes back to issuing a
      // token at register.
      let target;
      if (isMerchant)      target = from || '/merchant';
      else if (isPartner)  target = from || '/admin';
      else                 target = from || '/coupons';
      navigate(target, { replace: true });
    } else {
      // Spread Laravel field-level errors into the form so each shows up
      // under the right input ("The email has already been taken." under
      // Email, etc), while the general headline stays at the top of the
      // form for callers that just look at errors.general.
      setErrors({ general: result.error, ...(result.fieldErrors || {}) });
    }
  };

  const f = (key) => ({
    value: form[key],
    onChange: e => setForm(p => ({ ...p, [key]: e.target.value })),
  });

  const brandContent = isMerchant ? {
    title: 'Grow your brand with',
    titleAccent: 'high-intent shoppers.',
    lead: 'Free to list. Pay only when PerkVora drives real sales. Live in 24 hours.',
    points: [
      { icon: '🎯', title: 'Reach ready-to-buy customers', desc: '3-5× higher conversion than display ads' },
      { icon: '📊', title: 'Real-time analytics', desc: 'Track impressions, clicks, redemptions, revenue' },
      { icon: '💸', title: 'Pay only for results', desc: 'Performance-based — no flat fees' },
    ],
  } : isPartner ? {
    title: 'Build on the',
    titleAccent: 'PerkVora API.',
    lead: '10,000+ verified deals. Real-time webhooks. Sandbox included. Ship in days, not months.',
    points: [
      { icon: '⚡', title: 'REST API + SDKs', desc: 'JS, PHP, Python, Ruby — fully typed and documented' },
      { icon: '🔔', title: 'Real-time webhooks', desc: 'coupon.created, redemption.completed, cashback.paid' },
      { icon: '🛡️', title: 'Enterprise-grade SLA', desc: '99.9% uptime, priority engineering support' },
    ],
  } : {
    title: 'Join thousands saving',
    titleAccent: 'every day.',
    lead: 'Create your free account in under 60 seconds. No credit card, no tricks, no spam — ever.',
    points: [
      { icon: '🏷️', title: '10,000+ verified deals', desc: 'Hand-tested codes that actually work' },
      { icon: '💰', title: 'Real cashback rewards', desc: 'Cash out straight to your bank via ACH' },
      { icon: '🔒', title: 'Your data stays yours', desc: 'We never sell your info. Period.' },
    ],
  };

  const heading = isMerchant ? 'List your business'
                : isPartner  ? 'Create a partner account'
                : 'Create your free account';
  const sub     = isMerchant ? 'Reach millions of shoppers. Takes under 5 minutes.'
                : isPartner  ? 'Get API access, sandbox keys, and docs in one place.'
                : 'Start saving in 60 seconds. Free forever.';

  return (
    <AuthShell side={<BrandPanel {...brandContent} />}>
      <AuthHeader title={heading} subtitle={sub} />

      {!isMerchant && !isPartner && <SocialButtons />}

      {errors.general && <div className="alert alert-danger">{errors.general}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">{isMerchant ? 'Business name' : 'Full name'} *</label>
          <input className="form-input" type="text" required autoFocus placeholder={isMerchant ? 'Acme Inc.' : 'Jane Doe'} {...f('name')} />
        </div>

        <div className="form-group">
          <label className="form-label">{isMerchant ? 'Business email' : 'Email address'} *</label>
          {/*
            Use type="text" + inputMode="email" instead of type="email" so
            the browser doesn't pop a native tooltip ("A part following '@'
            should not contain the symbol '='.") that breaks the PerkVora
            design language (Ananya 06-30 #11). Validation happens server-
            side and the server's error renders through our custom
            `errors.general` / `errors.email` slots below.
          */}
          <input
            className="form-input"
            type="text"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            {...f('email')}
          />
          {errors.email && <p className="form-error">{errors.email}</p>}
        </div>

        <div className="form-group">
          <label className="form-label">Password *</label>
          <PasswordInput
            value={form.password}
            onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
            placeholder="At least 8 characters"
          />
          {errors.password && <p className="form-error">{errors.password}</p>}
          <PasswordStrength password={form.password} />
        </div>

        <div className="form-group">
          <label className="form-label">Confirm password *</label>
          <input className="form-input" type="password" required placeholder="Re-enter your password" {...f('password_confirmation')} />
          {errors.password_confirmation && <p className="form-error">{errors.password_confirmation}</p>}
        </div>

        {/* Optional referral / redemption code. Auto-expanded when a ?ref=
            arrived from a share link; collapsed otherwise so the form
            stays short for the 99% of users without a code. */}
        {showRefInput ? (
          <div className="form-group">
            <label className="form-label">Referral code <span style={{ color: '#8B94A8', fontWeight: 400 }}>(optional)</span></label>
            <input
              className="form-input"
              type="text"
              placeholder="ABCD1234"
              maxLength={16}
              value={form.ref}
              onChange={e => setForm(p => ({ ...p, ref: e.target.value.toUpperCase() }))}
              style={{ letterSpacing: 1, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
              autoComplete="off"
            />
            {errors.ref && <p className="form-error">{errors.ref}</p>}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowRefInput(true)}
            style={{ background: 'none', border: 'none', color: '#6B2FD9', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 16, alignSelf: 'flex-start', fontFamily: 'inherit' }}
          >
            + Have a referral code?
          </button>
        )}

        <label style={{ display: 'flex', gap: 10, fontSize: 13, color: '#4A5568', marginBottom: 12, cursor: 'pointer', lineHeight: 1.5 }}>
          <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: '#6B2FD9', marginTop: 2, flexShrink: 0 }} />
          <span>
            {/* Plain <a target="_blank"> on purpose — React Router's Link
               does client-side nav and would clobber the half-filled
               signup form. New tab keeps the user's data intact. */}
            I agree to PerkVora's <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#6B2FD9', fontWeight: 600 }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#6B2FD9', fontWeight: 600 }}>Privacy Policy</a>.
          </span>
        </label>
        {errors.terms && <p className="form-error" style={{ marginBottom: 12 }}>{errors.terms}</p>}

        <label style={{ display: 'flex', gap: 10, fontSize: 13, color: '#4A5568', marginBottom: 24, cursor: 'pointer', lineHeight: 1.5 }}>
          <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: '#6B2FD9', marginTop: 2, flexShrink: 0 }} />
          <span>Send me weekly deal highlights and cashback bonuses. <span style={{ color: '#8B94A8' }}>(You can unsubscribe anytime.)</span></span>
        </label>

        <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}>
          {loading ? 'Creating account…'
            : isMerchant ? 'Start listing deals →'
            : isPartner  ? 'Get API access →'
            : 'Create free account →'}
        </button>
      </form>

      <p style={{ textAlign: 'center', fontSize: 14, color: '#4A5568', marginTop: 24 }}>
        Already have an account?{' '}
        <Link to="/login" state={{ from }} style={{ color: '#6B2FD9', fontWeight: 600 }}>Sign in</Link>
      </p>
    </AuthShell>
  );
}

/* ============================= FORGOT PASSWORD ============================= */

export function ForgotPasswordPage() {
  const [email, setEmail]     = useState('');
  const [status, setStatus]   = useState('idle'); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      const recaptcha_token = await executeRecaptcha('forgot_password');
      await api.post('/auth/forgot-password', { email, recaptcha_token });
      setStatus('done');
    } catch (err) {
      // The backend deliberately doesn't reveal whether the address exists,
      // so the only errors that surface here are validation (bad email) and
      // network / 5xx. Treat both the same way to the user.
      setErrorMsg(err.response?.data?.errors?.email?.[0] || 'Something went wrong. Please try again in a minute.');
      setStatus('error');
    }
  };

  return (
    <AuthShell side={
      <BrandPanel
        title="Reset your password,"
        titleAccent="we'll get you back in."
        lead="Enter the email on your account. If it's registered, we'll send a one-time link to choose a new password."
        points={[
          { icon: '⏱', title: 'Link expires in 30 minutes', desc: 'For your security, links are short-lived.' },
          { icon: '🛡', title: 'Single-use only', desc: 'Each link works exactly once and dies on use.' },
          { icon: '🔒', title: 'Sessions invalidated', desc: 'On reset, every existing login is logged out.' },
        ]}
      />
    }>
      <AuthHeader title="Forgot your password?" subtitle="No problem — type your email and we'll send a reset link." />

      {status === 'done' ? (
        <>
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            ✓ If that email is registered, a reset link is on its way. Check your inbox in a minute or two.
          </div>
          <p style={{ fontSize: 13, color: '#4A5568', lineHeight: 1.6, marginBottom: 24 }}>
            Didn't get it? Check your spam folder. Still nothing after 5 minutes — try again
            or <Link to="/contact" style={{ color: '#6B2FD9', fontWeight: 600 }}>contact support</Link>.
          </p>
          <Link to="/login" className="btn btn-secondary btn-full">← Back to sign in</Link>
        </>
      ) : (
        <>
          {status === 'error' && <div className="alert alert-danger">{errorMsg}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input className="form-input" type="text" inputMode="email" autoComplete="email" required autoFocus value={email}
                onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                placeholder="you@example.com" disabled={status === 'submitting'} />
            </div>
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Sending…' : 'Send reset link →'}
            </button>
          </form>
          <p style={{ textAlign: 'center', fontSize: 14, color: '#4A5568', marginTop: 28 }}>
            Remembered it? <Link to="/login" style={{ color: '#6B2FD9', fontWeight: 600 }}>Back to sign in</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}

/* ============================= RESET PASSWORD ============================= */

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [form, setForm]     = useState({ password: '', password_confirmation: '' });
  const [status, setStatus] = useState(token ? 'idle' : 'missing-token'); // idle | submitting | done | error | missing-token
  const [errorMsg, setErrorMsg] = useState('');

  // If the URL has no token, redirect to forgot-password after a beat so the
  // user can recover without hitting back.
  useEffect(() => {
    if (status === 'missing-token') {
      const t = setTimeout(() => navigate('/forgot-password', { replace: true }), 3000);
      return () => clearTimeout(t);
    }
  }, [status, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    if (form.password !== form.password_confirmation) {
      setErrorMsg('Passwords don\'t match.');
      setStatus('error');
      return;
    }
    if (form.password.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      await api.post('/auth/reset-password', {
        token,
        password: form.password,
        password_confirmation: form.password_confirmation,
      });
      setStatus('done');
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.response?.data?.message || 'This reset link is invalid or has expired.');
      setStatus('error');
    }
  };

  return (
    <AuthShell side={
      <BrandPanel
        title="Almost there —"
        titleAccent="choose a new password."
        lead="Pick something strong. Once you save, every device signed in to your account will be signed out for security."
        points={[
          { icon: '🔐', title: 'Use 12+ characters', desc: 'Longer beats clever-but-short every time.' },
          { icon: '🎲', title: 'Mix it up', desc: 'A password manager can generate one for you.' },
          { icon: '🚪', title: 'All sessions logged out', desc: 'Save = everything signed out, including this browser.' },
        ]}
      />
    }>
      <AuthHeader title="Choose a new password" subtitle="Pick something only you would think of." />

      {status === 'missing-token' && (
        <>
          <div className="alert alert-danger">
            This page needs a reset token. Redirecting you to the Forgot Password page…
          </div>
          <Link to="/forgot-password" className="btn btn-primary btn-full">Go now →</Link>
        </>
      )}

      {status === 'done' && (
        <>
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            ✓ Password updated. Sign in with your new password to continue.
          </div>
          <Link to="/login" className="btn btn-primary btn-full btn-lg">Go to sign in →</Link>
        </>
      )}

      {(status === 'idle' || status === 'submitting' || status === 'error') && (
        <>
          {status === 'error' && <div className="alert alert-danger">{errorMsg}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">New password</label>
              <PasswordInput
                value={form.password}
                onChange={e => { setForm(p => ({ ...p, password: e.target.value })); if (status === 'error') setStatus('idle'); }}
                placeholder="At least 8 characters"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm new password</label>
              <PasswordInput
                value={form.password_confirmation}
                onChange={e => { setForm(p => ({ ...p, password_confirmation: e.target.value })); if (status === 'error') setStatus('idle'); }}
                placeholder="Type it again"
              />
            </div>
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Updating…' : 'Update password →'}
            </button>
          </form>
          <p style={{ textAlign: 'center', fontSize: 14, color: '#4A5568', marginTop: 28 }}>
            Changed your mind? <Link to="/login" style={{ color: '#6B2FD9', fontWeight: 600 }}>Back to sign in</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}

/* ============================= VERIFY OTP ============================= */

/**
 * 6-digit code entry. Two ways the user lands here:
 *   1. From RegisterPage — fresh signup, never had a verified email.
 *   2. From LoginPage — credentials matched but email_verified_at is null
 *      (the backend issues a fresh OTP and returns 403 in that case).
 *
 * Both paths drop the email + an optional dev_otp into router state. We
 * also accept ?email=... in the URL so a user who lost the tab can reload
 * /verify-otp?email=foo@bar.com without re-registering.
 *
 * The yellow dev banner only renders when dev_otp is truthy — production
 * + staging never expose the code in the JSON response, so the banner
 * silently stays hidden there.
 */
export function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { verifyOtp, resendOtp, loading } = useAuth();

  const stateEmail = location.state?.email || '';
  const queryEmail = searchParams.get('email') || '';
  const email = stateEmail || queryEmail;

  const [devOtp, setDevOtp] = useState(location.state?.devOtp || null);
  const from = location.state?.from || '/coupons';

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const inputsRef = useRef([]);

  const [error, setError]     = useState('');
  const [status, setStatus]   = useState('idle');     // idle | verifying | done
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | error
  const [resendNote, setResendNote]   = useState('');

  // Send-the-user-back guard: if there's no email at all, we have nothing
  // to verify. Bounce to /login after a beat so they can try again.
  useEffect(() => {
    if (!email) {
      const t = setTimeout(() => navigate('/login', { replace: true }), 2500);
      return () => clearTimeout(t);
    }
  }, [email, navigate]);

  useEffect(() => {
    // Focus the first empty input on mount / when digits change so paste
    // and arrow-key nav both feel natural.
    const firstEmpty = digits.findIndex(d => !d);
    const idx = firstEmpty === -1 ? digits.length - 1 : firstEmpty;
    inputsRef.current[idx]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (i, v) => {
    // Only accept a single 0–9. If someone pastes "123456" into one input,
    // splatter it across all six (handled in the paste handler).
    const sanitized = v.replace(/[^0-9]/g, '').slice(0, 1);
    setDigits(prev => {
      const next = [...prev];
      next[i] = sanitized;
      return next;
    });
    if (sanitized && i < 5) {
      inputsRef.current[i + 1]?.focus();
    }
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft'  && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/[^0-9]/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    const lastFilled = Math.min(text.length, 6) - 1;
    inputsRef.current[Math.min(lastFilled + 1, 5)]?.focus();
  };

  const useDevCode = () => {
    if (!devOtp) return;
    const padded = String(devOtp).padStart(6, '0').slice(0, 6).split('');
    setDigits(padded.concat(Array(6 - padded.length).fill('')));
    inputsRef.current[5]?.focus();
  };

  const copyDevCode = async () => {
    if (!devOtp) return;
    try {
      await navigator.clipboard.writeText(String(devOtp));
      setResendNote('Code copied to clipboard.');
      setTimeout(() => setResendNote(''), 2000);
    } catch {
      // Clipboard API can fail on insecure origins / older browsers; ignore.
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    const code = digits.join('');
    if (code.length !== 6) {
      setError('Enter all six digits.');
      return;
    }
    setError('');
    setStatus('verifying');
    const result = await verifyOtp(email, code);
    if (result.success) {
      setStatus('done');
      const role = result.user?.role;
      let target;
      if (role === 'super_admin')       target = '/admin';
      else if (role === 'merchant_admin') target = '/merchant';
      else                                target = from || '/coupons';
      navigate(target, { replace: true });
    } else {
      setStatus('idle');
      setError(result.error || 'That code didn\'t match. Double-check and try again.');
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResendState('sending');
    setResendNote('');
    setError('');
    const result = await resendOtp(email);
    if (result.success) {
      setResendState('sent');
      setResendNote(result.message || 'A fresh code is on the way.');
      if (result.devOtp) setDevOtp(result.devOtp); // refresh the banner
      // Clear the current entry so the user types the new one fresh.
      setDigits(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
      setTimeout(() => setResendState('idle'), 4000);
    } else {
      setResendState('error');
      setError(result.error || 'Could not resend right now.');
    }
  };

  // Submit automatically once all six digits are present — saves a click
  // for users who paste the whole code in one go.
  useEffect(() => {
    if (status === 'verifying') return;
    if (digits.every(d => d.length === 1)) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  return (
    <AuthShell side={
      <BrandPanel
        title="One quick check —"
        titleAccent="verify your email."
        lead="We sent a 6-digit code to your inbox. Pop it in below and you're all set to start saving."
        points={[
          { icon: '⏱', title: 'Code expires in 10 minutes', desc: 'Short window keeps your account safe.' },
          { icon: '📩', title: 'Didn\'t get it? Resend below', desc: 'We\'ll fire a fresh code your way.' },
          { icon: '🔒', title: 'One-time use', desc: 'Each code works exactly once.' },
        ]}
      />
    }>
      <AuthHeader
        title="Verify your email"
        subtitle={email
          ? <>We sent a 6-digit code to <strong style={{ color: '#0F1225' }}>{email}</strong>. Enter it below to continue.</>
          : 'No email on file — redirecting you back to sign in.'}
      />

      {devOtp && (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: '1px solid #F59E0B',
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 20,
            fontSize: 13,
            color: '#7C2D12',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden="true">🛠️</span>
            <span>Dev mode — email not sent</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
            <button
              type="button"
              onClick={copyDevCode}
              title="Click to copy"
              style={{
                fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                fontSize: 22, fontWeight: 800, letterSpacing: '0.3em',
                background: '#fff', color: '#92400E', border: '1px solid #F59E0B',
                borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
              }}
            >
              {String(devOtp).padStart(6, '0')}
            </button>
            <button
              type="button"
              onClick={useDevCode}
              style={{
                background: '#92400E', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 14px', fontSize: 12,
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              Use this code →
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#9A3412', margin: 0 }}>
            Stage and production environments still email this code normally.
          </p>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {resendNote && resendState !== 'error' && <div className="alert alert-success" style={{ marginBottom: 16 }}>{resendNote}</div>}

      <form onSubmit={handleVerify}>
        <div className="form-group">
          <label className="form-label">6-digit verification code</label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => inputsRef.current[i] = el}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                onChange={e => setDigit(i, e.target.value)}
                onKeyDown={e => onKeyDown(i, e)}
                onPaste={i === 0 ? onPaste : undefined}
                aria-label={`Digit ${i + 1}`}
                disabled={status === 'verifying' || status === 'done'}
                style={{
                  width: '100%', maxWidth: 56, height: 60,
                  textAlign: 'center', fontSize: 24, fontWeight: 700,
                  border: '1.5px solid #E4E7F0', borderRadius: 12,
                  background: '#fff', color: '#0F1225',
                  fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                  outline: 'none', transition: 'all 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = '#6B2FD9'; e.target.style.boxShadow = '0 0 0 3px rgba(107,47,217,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#E4E7F0'; e.target.style.boxShadow = 'none'; }}
              />
            ))}
          </div>
        </div>

        <button
          className="btn btn-primary btn-full btn-lg"
          type="submit"
          disabled={status === 'verifying' || status === 'done' || loading || !email}
          style={{ marginTop: 8 }}
        >
          {status === 'verifying' ? 'Verifying…' : status === 'done' ? 'Verified ✓' : 'Verify email →'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#4A5568' }}>
        Didn't get the code?{' '}
        <button
          type="button"
          onClick={handleResend}
          disabled={resendState === 'sending' || !email}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: '#6B2FD9', fontWeight: 600, cursor: 'pointer',
            fontSize: 14, fontFamily: 'inherit',
          }}
        >
          {resendState === 'sending' ? 'Sending…' : 'Resend code'}
        </button>
      </div>

      <p style={{ textAlign: 'center', fontSize: 13, color: '#8B94A8', marginTop: 16 }}>
        Wrong email? <Link to="/register" style={{ color: '#6B2FD9', fontWeight: 600 }}>Start over</Link>
      </p>
    </AuthShell>
  );
}

/* ============================= icons ============================= */

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.1-11.3-7.8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.2 5.3C41.2 35.4 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}
